import React, { useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Headset, History, Loader2, MessageCircle, UserPlus } from 'lucide-react';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import useApi from '../api';
import VisitorIdentity from '../VisitorIdentity';
import { dedupeActivity } from './liveViewModel';

interface VisitorPresence {
  visitor_id: string;
  agent_id: string;
  organization_id: string;
  current_page?: string | null;
  status: 'active' | 'inactive';
  last_seen: string;
  conversation_id?: string;
  session_active?: boolean;
  click_events?: Array<{ parent_window_url: string; timestamp: string; x?: number; y?: number; element?: string; element_name?: string; element_text?: string }>;
  page_view_history?: Array<{ page: string; timestamp: string }>;
  ip_address?: string;
  city?: string;
  region?: string;
  country?: string;
  conversation_count?: number;
  latest_conversation_id?: string;
  is_warm?: boolean;
  warm_reasons?: string[];
  human_agent?: {
    id: string;
    name?: string;
    email?: string;
  };
  online?: boolean;
  last_seen_at?: string | null;
  first_seen_at?: string | null;
  identity?: any;
}

interface VisitorDrawerProps {
  visitor: VisitorPresence | null;
  onClose: () => void;
  onNavigateToSession: (conversationId: string) => void;
  onOpenChat: (visitorId: string, conversationId: string) => void;
  onStartChat: (visitorId: string) => void;
  onViewSessions: (visitorId: string) => void;
  onContactSynced?: () => void;
}

interface VisitorActivity {
  page_views?: Array<{ page?: string; url?: string; timestamp: string }>;
  click_events?: Array<{ parent_window_url: string; timestamp: string; x?: number; y?: number; element?: string; element_name?: string; element_text?: string }>;
  page_visit_counts?: Array<{ url?: string | null; path?: string | null; count: number; last_visited_at?: string | null }>;
  current_page?: string | null;
  online?: boolean;
}

const VisitorDrawer: React.FC<VisitorDrawerProps> = ({
  visitor,
  onClose,
  onNavigateToSession,
  onOpenChat,
  onStartChat,
  onViewSessions,
  onContactSynced,
}) => {
  const { get, post } = useApi();
  const [syncingContact, setSyncingContact] = useState(false);
  const [syncedLeadId, setSyncedLeadId] = useState<string | null>(null);
  const [activity, setActivity] = useState<VisitorActivity | null>(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityError, setActivityError] = useState(false);

  const visitorId = visitor?.visitor_id;
  const isContact = Boolean(visitor?.identity?.lead?.id);

  useEffect(() => {
    let cancelled = false;
    setActivity(null);
    setActivityError(false);

    if (!visitorId || isContact) {
      setLoadingActivity(false);
      return () => {
        cancelled = true;
      };
    }

    setLoadingActivity(true);
    void get(`/visitor-profiles/${visitorId}/activity`)
      .then((response) => {
        if (!cancelled) setActivity(response.data || null);
      })
      .catch(() => {
        if (!cancelled) setActivityError(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingActivity(false);
      });

    return () => {
      cancelled = true;
    };
  }, [get, isContact, visitorId]);

  const contactLead = visitor?.identity?.lead || null;
  const browsingActivity = useMemo(() => {
    if (isContact || !visitor) return null;
    const pageViews = dedupeActivity(
      [
        ...(activity?.page_views || []).map((view) => ({
          page: view.page || view.url || '',
          timestamp: view.timestamp,
        })),
        ...(visitor.page_view_history || []),
      ],
      (view) => `${view.timestamp}:${view.page}`,
      50,
    );
    const clickEvents = dedupeActivity(
      [...(activity?.click_events || []), ...(visitor.click_events || [])],
      (click) => `${click.timestamp}:${click.parent_window_url}:${click.element || ''}`,
      100,
    );
    return {
      pageViews,
      clickEvents,
      pageVisitCounts: (activity?.page_visit_counts || [])
        .filter((entry) => Boolean(entry.url || entry.path))
        .map((entry) => ({
          ...entry,
          url: entry.url || entry.path || '',
        })),
      currentPage: visitor.current_page || activity?.current_page || null,
      online: visitor.online ?? activity?.online ?? false,
    };
  }, [activity, isContact, visitor]);

  if (!visitor) return null;

  // A profile with an email/phone but no linked contact can be synced
  // manually — recovery path for contact info that never became a lead.
  // unverified_email counts: the human clicking sync overrides the verifier.
  const profileId = visitor.identity?.visitor_profile?.id || null;
  const collectedFields: Array<{ key?: string; value?: any }> =
    visitor.identity?.collected_fields || [];
  const hasContactInfo = collectedFields.some(
    (entry) =>
      (entry.key === 'email' || entry.key === 'phone' || entry.key === 'unverified_email') &&
      entry.value
  );
  const canSyncContact = !isContact && !syncedLeadId && Boolean(profileId) && hasContactInfo;

  const handleSyncContact = async () => {
    if (!profileId || syncingContact) return;
    setSyncingContact(true);
    try {
      const response = await post(`/visitor-profiles/${profileId}/sync-lead`, {});
      const leadId = response?.data?.lead_id || null;
      setSyncedLeadId(leadId);
      toast.success(
        response?.data?.is_new ? 'Contact created and synced.' : 'Contact synced.',
        toastConfig
      );
      onContactSynced?.();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to sync contact. Please try again.', toastConfig);
    } finally {
      setSyncingContact(false);
    }
  };

  return (
    <Sheet open={visitor !== null} onOpenChange={onClose}>
      <SheetContent className="min-w-[50%] grow overflow-y-auto overflow-x-hidden">
        {/* Identity — reuses the same component sessions use */}
        <VisitorIdentity
          leadId={contactLead?.id || syncedLeadId || undefined}
          preloadedIdentity={isContact || syncedLeadId ? null : visitor.identity || null}
          lead={contactLead}
          browsingActivity={browsingActivity}
          clickable={true}
          mode="full"
        />
        {loadingActivity && (
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading browsing activity…
          </div>
        )}
        {activityError && (
          <p className="mt-3 text-xs text-muted-foreground">
            Browsing activity could not be loaded.
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mt-4">
          {canSyncContact && (
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              disabled={syncingContact}
              onClick={handleSyncContact}
            >
              {syncingContact ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <UserPlus className="h-3.5 w-3.5 mr-1.5" />
              )}
              Sync Contact
            </Button>
          )}
          {visitor.online && visitor.session_active && visitor.conversation_id ? (
            <Button
              size="sm"
              className="flex-1"
              onClick={() => onNavigateToSession(visitor.conversation_id!)}
            >
              <Headset className="h-3.5 w-3.5 mr-1.5" />
              Take Over Chat
            </Button>
          ) : visitor.online && visitor.latest_conversation_id ? (
            // Existing conversation: go to the session and take over there —
            // the visitor's chat reopens when the rep sends their first
            // message (not before), matching the Slack takeover behavior.
            <Button
              size="sm"
              className="flex-1"
              onClick={() => onNavigateToSession(visitor.latest_conversation_id!)}
            >
              <Headset className="h-3.5 w-3.5 mr-1.5" />
              Take Over Chat
            </Button>
          ) : visitor.online ? (
            <Button
              size="sm"
              className="flex-1"
              onClick={() => onStartChat(visitor.visitor_id)}
            >
              Start Chat
            </Button>
          ) : visitor.latest_conversation_id ? (
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => onNavigateToSession(visitor.latest_conversation_id!)}
            >
              <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
              View Last Session
            </Button>
          ) : null}
          {(visitor.conversation_count || 0) > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => onViewSessions(visitor.visitor_id)}
            >
              <History className="h-3.5 w-3.5 mr-1.5" />
              View all sessions
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default VisitorDrawer;
